/**
 * Menu hotkeys (Character C, Talents N, Inventory B, Gems J, Vault V, Rank K, Settings O,
 * Meters M, Keybind hints H, hide UI Alt+Z, Release X while dead, Esc closes an open overlay;
 * +field: Auto Z and Talk T in a world session).
 *
 * `routeMenuKey` is the pure routing table (unit-tested). `menuHotkeys` is the one window
 * listener: installed at startup (meta/metaBridge.ts) so it runs in the capture phase BEFORE the
 * game's input listener, and acts synchronously on keydown (screens open the same frame). Keys it
 * handles are consumed, so Esc closing an overlay does not also clear the target.
 *
 * Two sides plug in: the meta router (`meta`, route keys: overlays in a run, normal navigation in
 * the hub) and the in-run HUD (`hud`, set by the menu widget while a run is live).
 */

import { fieldView } from '../../world/fieldView'; // +field
import { profView } from '../../world/profView'; // +prof

export type MenuRoute = 'character' | 'talents' | 'inventory' | 'gems' | 'vault' | 'rank';

export type MenuCommand =
  | { kind: 'route'; route: MenuRoute }
  /** Esc with an overlay open: back one screen (closing the overlay from its bottom). */
  | { kind: 'back' }
  | { kind: 'settings' }
  | { kind: 'meters' }
  | { kind: 'hints' }
  | { kind: 'toggleUi' }
  /** Release spirit (only while dead). */
  | { kind: 'release' }
  /** +field: toggle Auto (world sessions). */
  | { kind: 'auto' }
  /** +field: talk to the town NPC in reach. */
  | { kind: 'interact' }
  /** +prof: the Professions panel (world sessions). */
  | { kind: 'professions' }
  /** +quests: the quest log (world sessions). */
  | { kind: 'questLog' };

export interface MenuKeyDef {
  /** KeyboardEvent.code (layout independent). */
  code: string;
  alt?: boolean;
  /** Label for key hints / tooltips ("C", "Alt+Z"). */
  label: string;
  command: MenuCommand;
}

const route = (r: MenuRoute): MenuCommand => ({ kind: 'route', route: r });

/** The menu keymap. None of these codes is a game key (input/bindings.ts DEFAULT_KEYMAP, P = pause). */
export const MENU_KEYS: readonly MenuKeyDef[] = [
  { code: 'KeyC', label: 'C', command: route('character') },
  { code: 'KeyN', label: 'N', command: route('talents') },
  { code: 'KeyB', label: 'B', command: route('inventory') },
  { code: 'KeyJ', label: 'J', command: route('gems') },
  { code: 'KeyV', label: 'V', command: route('vault') },
  { code: 'KeyK', label: 'K', command: route('rank') },
  { code: 'KeyO', label: 'O', command: { kind: 'settings' } },
  { code: 'KeyM', label: 'M', command: { kind: 'meters' } },
  { code: 'KeyH', label: 'H', command: { kind: 'hints' } },
  { code: 'KeyZ', alt: true, label: 'Alt+Z', command: { kind: 'toggleUi' } },
  { code: 'KeyX', label: 'X', command: { kind: 'release' } },
  { code: 'KeyZ', label: 'Z', command: { kind: 'auto' } }, // +field
  { code: 'KeyT', label: 'T', command: { kind: 'interact' } }, // +field
  { code: 'KeyL', label: 'L', command: { kind: 'professions' } }, // +prof (L left the Attack-key choices)
  { code: 'KeyY', label: 'Y', command: { kind: 'questLog' } }, // +quests (Y left the Attack-key choices)
];

/** Hint label of a route's key ("" when unbound). */
export function routeKeyLabel(r: MenuRoute): string {
  return MENU_KEYS.find((d) => d.command.kind === 'route' && d.command.route === r)?.label ?? '';
}

/** Hint label of a non-route command's key. */
export function commandKeyLabel(kind: Exclude<MenuCommand['kind'], 'route'>): string {
  return MENU_KEYS.find((d) => d.command.kind === kind)?.label ?? '';
}

export interface KeyLike {
  code: string;
  altKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  repeat?: boolean;
}

export interface MenuKeyState {
  /** A run is live (the HUD menu is registered and the key is not over). */
  inRun: boolean;
  /** Meta screens are shown as an overlay over the run. */
  overlayOpen: boolean;
  /** The meta UI (hub or a screen) is showing outside a run. */
  screenOpen: boolean;
  /** Focus is in a text field / select. */
  typing: boolean;
  /** A modal dialog is open (it owns Esc and the keyboard). */
  modalOpen: boolean;
  /** The player is dead (Release is available). */
  dead?: boolean;
  /** +field: a world session (field / town / levelled dungeon) is running. */
  world?: boolean;
}

/** What a keydown means for the menus (null = not ours; let the game have it). */
export function routeMenuKey(e: KeyLike, s: MenuKeyState): MenuCommand | null {
  if (s.typing || e.repeat || e.ctrlKey || e.metaKey) return null;
  if (e.code === 'Escape') return s.overlayOpen && !s.modalOpen && !e.altKey && !e.shiftKey ? { kind: 'back' } : null;
  if (s.modalOpen || e.shiftKey) return null;
  const def = MENU_KEYS.find((d) => d.code === e.code && !!d.alt === !!e.altKey);
  if (!def) return null;
  if (def.command.kind === 'release') return s.inRun && s.dead ? def.command : null;
  // +field: only in a world session with nothing open on top (an overlay owns the keyboard).
  if (def.command.kind === 'auto' || def.command.kind === 'interact' || def.command.kind === 'professions' || def.command.kind === 'questLog') return s.inRun && s.world && !s.overlayOpen ? def.command : null;
  if (def.command.kind !== 'route') return s.inRun ? def.command : null;
  return s.inRun || s.screenOpen ? def.command : null;
}

/** In-run HUD side (the menu widget). */
export interface HudHotkeyHandlers {
  /** False once the key is over (end screen pending): menus stop opening overlays. */
  active(): boolean;
  settings(): void;
  meters(): void;
  hints(): void;
  toggleUi(): void;
  dead(): boolean;
  release(): void;
}

/** Meta router side (meta/metaBridge.ts). */
export interface MetaHotkeyTarget {
  overlayOpen(): boolean;
  screenOpen(): boolean;
  /** Open `route` (an overlay when `inRun`), or close it if it is already on top. */
  toggle(route: MenuRoute, inRun: boolean): void;
  back(): void;
}

function isTyping(t: EventTarget | null): boolean {
  const e = t as HTMLElement | null;
  if (!e || typeof e.tagName !== 'string') return false;
  return e.tagName === 'INPUT' || e.tagName === 'TEXTAREA' || e.tagName === 'SELECT' || e.isContentEditable === true;
}

class MenuHotkeys {
  hud: HudHotkeyHandlers | null = null;
  meta: MetaHotkeyTarget | null = null;
  private installed = false;

  state(typing = false): MenuKeyState {
    const inRun = !!this.hud && this.hud.active();
    return {
      inRun,
      overlayOpen: inRun && !!this.meta?.overlayOpen(),
      screenOpen: !inRun && !!this.meta?.screenOpen(),
      typing,
      dead: inRun && !!this.hud?.dead(),
      world: inRun && fieldView.active && !!fieldView.actions,
      modalOpen: typeof document !== 'undefined' && !!document.querySelector('.kui-modal-backdrop, .km-modal-back'),
    };
  }

  /** Run a command (also used by the menu buttons, so a click and a key do the same thing). */
  run(cmd: MenuCommand): void {
    const inRun = !!this.hud && this.hud.active();
    switch (cmd.kind) {
      case 'route':
        return this.meta?.toggle(cmd.route, inRun);
      case 'back':
        return this.meta?.back();
      case 'settings':
        return this.hud?.settings();
      case 'meters':
        return this.hud?.meters();
      case 'hints':
        return this.hud?.hints();
      case 'toggleUi':
        return this.hud?.toggleUi();
      case 'release':
        return this.hud?.release();
      case 'auto':
        return fieldView.actions?.toggleAuto();
      case 'interact': {
        const p = fieldView.prompt;
        return p ? fieldView.actions?.interact(p.npcId) : undefined;
      }
      case 'professions':
        return profView.actions?.togglePanel(); // +prof
      case 'questLog':
        return fieldView.actions?.toggleQuestLog?.(); // +quests
    }
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Cheap reject first: most keys are game keys.
    if (e.code !== 'Escape' && !CODES.has(e.code)) return;
    const cmd = routeMenuKey(e, this.state(isTyping(e.target)));
    if (!cmd) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    this.run(cmd);
  };

  /** Install the window listener once (capture phase, before the game's input). */
  install(): void {
    if (this.installed || typeof window === 'undefined') return;
    this.installed = true;
    window.addEventListener('keydown', this.onKeyDown, { capture: true });
  }
}

const CODES = new Set(MENU_KEYS.map((d) => d.code));

/** The app's single menu-hotkey dispatcher. */
export const menuHotkeys = new MenuHotkeys();
